import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import { format } from 'date-fns';
import {
  Bell, Send, Users as UsersIcon, Truck, Store, Globe,
  Search, X, Trash2, Image as ImageIcon, AlertTriangle, CheckCircle, RotateCw
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AUDIENCE_META = {
  ALL:         { label: 'All Audiences',  icon: Globe,     color: '#7C3AED', bg: '#F5F3FF' },
  USERS:       { label: 'Users',          icon: UsersIcon, color: '#3B82F6', bg: '#EFF6FF' },
  AGENTS:      { label: 'Delivery Agents', icon: Truck,    color: '#10B981', bg: '#ECFDF5' },
  RESTAURANTS: { label: 'Restaurants',    icon: Store,     color: '#C2410C', bg: '#FFF7ED' }
};

const Notifications = () => {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [audienceFilter, setAudienceFilter] = useState('all');

  // Compose
  const [showCompose, setShowCompose] = useState(false);
  const [audience, setAudience] = useState('USERS');
  const [title, setTitle] = useState('');
  const [message, setMessage] = useState('');
  const [imageUrl, setImageUrl] = useState('');
  const [recipientMode, setRecipientMode] = useState('everyone'); // everyone | specific
  const [recipientQuery, setRecipientQuery] = useState('');
  const [recipientResults, setRecipientResults] = useState([]);
  const [selectedRecipients, setSelectedRecipients] = useState([]); // [{id, name, phone, email}]
  const [sending, setSending] = useState(false);

  const fetchHistory = async () => {
    try {
      const res = await axios.get(`${API}/notifications`);
      setHistory(res.data || []);
    } catch (err) {
      toast.error('Failed to load notifications');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchHistory(); }, []);

  // Recipient lookup (debounced via effect)
  useEffect(() => {
    if (recipientMode !== 'specific' || audience === 'ALL') {
      setRecipientResults([]);
      return;
    }
    const t = setTimeout(async () => {
      try {
        const res = await axios.get(`${API}/notifications/recipients`, {
          params: { audience, q: recipientQuery }
        });
        setRecipientResults(res.data || []);
      } catch (err) {
        setRecipientResults([]);
      }
    }, 250);
    return () => clearTimeout(t);
  }, [recipientQuery, audience, recipientMode]);

  const toggleRecipient = (r) => {
    setSelectedRecipients((cur) => {
      const exists = cur.find((x) => x.id === r.id);
      if (exists) return cur.filter((x) => x.id !== r.id);
      return [...cur, r];
    });
  };

  const resetCompose = () => {
    setAudience('USERS');
    setTitle('');
    setMessage('');
    setImageUrl('');
    setRecipientMode('everyone');
    setRecipientQuery('');
    setRecipientResults([]);
    setSelectedRecipients([]);
  };

  const send = async () => {
    if (!title.trim() || !message.trim()) {
      toast.error('Title and message are required');
      return;
    }
    setSending(true);
    try {
      const targetIds = recipientMode === 'specific'
        ? selectedRecipients.map((r) => r.id)
        : [];
      if (recipientMode === 'specific' && targetIds.length === 0 && audience !== 'ALL') {
        toast.error('Pick at least one recipient or switch to "Everyone"');
        setSending(false);
        return;
      }
      const res = await axios.post(`${API}/notifications`, {
        title, message, audience, imageUrl: imageUrl || undefined, targetIds
      });
      toast.success(`Sent to ${res.data?.recipientCount || 0} recipient(s)`);
      setShowCompose(false);
      resetCompose();
      fetchHistory();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send');
    } finally {
      setSending(false);
    }
  };

  const remove = async (n) => {
    if (!window.confirm('Delete this notification record?')) return;
    try {
      await axios.delete(`${API}/notifications/${n.id}`);
      toast.success('Deleted');
      fetchHistory();
    } catch (err) {
      toast.error('Failed to delete');
    }
  };

  const [resendingId, setResendingId] = useState(null);
  const resend = async (n) => {
    try {
      setResendingId(n.id);
      const res = await axios.post(`${API}/notifications/${n.id}/resend`);
      toast.success(res.data?.message || 'Resend queued');
      // Refresh after a short delay so delivery stats can update
      setTimeout(fetchHistory, 1500);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to resend');
    } finally {
      setResendingId(null);
    }
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return history.filter((n) => {
      if (audienceFilter !== 'all' && n.audience !== audienceFilter) return false;
      if (q) {
        const hay = [n.title, n.message, n.sentByName].filter(Boolean).map((v) => v.toLowerCase());
        if (!hay.some((v) => v.includes(q))) return false;
      }
      return true;
    });
  }, [history, search, audienceFilter]);

  // Stats
  const stats = useMemo(() => {
    let users = 0, agents = 0, rest = 0, all = 0;
    for (const n of history) {
      if (n.audience === 'USERS') users++;
      else if (n.audience === 'AGENTS') agents++;
      else if (n.audience === 'RESTAURANTS') rest++;
      else if (n.audience === 'ALL') all++;
    }
    return { total: history.length, users, agents, rest, all };
  }, [history]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary" />
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 lg:p-8" style={{ fontFamily: 'Public Sans, sans-serif' }}>
      <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
            <Bell size={26} className="text-orange-600" /> Notifications
          </h1>
          <p className="text-gray-600 text-sm mt-1">Send announcements to users, drivers and restaurants</p>
        </div>
        <Button data-testid="compose-notification-button" onClick={() => setShowCompose(true)} style={{ backgroundColor: '#C2410C' }} className="text-white">
          <Send size={16} className="mr-2" /> Compose
        </Button>
      </div>

      {/* TOP STATS */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6" data-testid="notif-stats">
        {[
          { label: 'Total Sent',   value: stats.total,  icon: Bell,      color: '#0F172A', bg: '#F1F5F9' },
          { label: 'To Users',     value: stats.users,  icon: UsersIcon, color: '#3B82F6', bg: '#EFF6FF' },
          { label: 'To Agents',    value: stats.agents, icon: Truck,     color: '#10B981', bg: '#ECFDF5' },
          { label: 'To Restaurants', value: stats.rest, icon: Store,     color: '#C2410C', bg: '#FFF7ED' },
          { label: 'Broadcast',    value: stats.all,    icon: Globe,     color: '#7C3AED', bg: '#F5F3FF' }
        ].map((s) => {
          const Icon = s.icon;
          return (
            <div key={s.label} className="bg-white rounded-xl border border-gray-200 p-4">
              <div className="flex items-center justify-between mb-2">
                <div className="p-2 rounded-lg" style={{ backgroundColor: s.bg }}>
                  <Icon size={18} style={{ color: s.color }} />
                </div>
              </div>
              <h3 className="text-2xl font-bold" style={{ color: '#0F172A' }}>{s.value}</h3>
              <p className="text-xs text-gray-600 mt-1">{s.label}</p>
            </div>
          );
        })}
      </div>

      {/* FCM warning banner */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-6 flex items-start gap-2">
        <AlertTriangle size={18} className="text-blue-600 flex-shrink-0 mt-0.5" />
        <div className="text-sm text-blue-900">
          <strong>Push delivery (FCM)</strong> runs in the background after each send. Configure the
          Firebase service account in <a href="/settings" className="underline font-semibold">Settings</a> if
          you haven't yet — until then notifications are only stored.
        </div>
      </div>

      {/* FILTER BAR */}
      <div className="bg-white rounded-xl border border-gray-200 p-4 mb-4 flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[260px] max-w-md">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <Input
            data-testid="notif-search-input"
            placeholder="Search title, message or sender..."
            className="pl-9 pr-9"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          {search && (
            <button onClick={() => setSearch('')} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
              <X size={16} />
            </button>
          )}
        </div>
        <Select value={audienceFilter} onValueChange={setAudienceFilter}>
          <SelectTrigger className="w-[180px]" data-testid="notif-audience-filter">
            <SelectValue placeholder="Audience" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All audiences</SelectItem>
            {Object.entries(AUDIENCE_META).map(([k, v]) => (
              <SelectItem key={k} value={k}>{v.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <div className="ml-auto text-sm text-gray-600">{filtered.length} of {history.length}</div>
      </div>

      {/* HISTORY LIST */}
      <div className="space-y-3">
        {filtered.length === 0 ? (
          <div className="bg-white border border-gray-200 rounded-xl p-10 text-center">
            <Bell className="mx-auto text-gray-300 mb-3" size={36} />
            <p className="text-gray-500">No notifications {history.length > 0 ? 'match your filters' : 'sent yet'}</p>
          </div>
        ) : (
          filtered.map((n) => {
            const meta = AUDIENCE_META[n.audience] || AUDIENCE_META.USERS;
            const Icon = meta.icon;
            return (
              <div key={n.id} className="bg-white border border-gray-200 rounded-xl p-5 flex gap-4" data-testid={`notif-row-${n.id}`}>
                <div className="w-12 h-12 rounded-full flex-shrink-0 flex items-center justify-center" style={{ backgroundColor: meta.bg }}>
                  <Icon size={20} style={{ color: meta.color }} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-center gap-2 mb-1">
                    <h3 className="font-semibold text-gray-900">{n.title}</h3>
                    <Badge className="border-0" style={{ backgroundColor: meta.bg, color: meta.color }}>
                      {meta.label}
                    </Badge>
                    {n.targetIds && n.targetIds.length > 0 && (
                      <Badge className="bg-gray-100 text-gray-700 border-0">
                        {n.targetIds.length} specific
                      </Badge>
                    )}
                    <Badge className="bg-emerald-100 text-emerald-700 border-0 flex items-center gap-1">
                      <CheckCircle size={11} />Stored
                    </Badge>
                    {n.deliveryStats && n.deliveryStats.total > 0 ? (
                      <Badge className="bg-emerald-50 text-emerald-800 border border-emerald-200">
                        Push: {n.deliveryStats.success}/{n.deliveryStats.total}
                        {n.deliveryStats.failure > 0 ? ` (${n.deliveryStats.failure} failed)` : ''}
                      </Badge>
                    ) : !n.pushDelivered ? (
                      <Badge className="bg-amber-100 text-amber-800 border-0">Push pending</Badge>
                    ) : null}
                    {n.resendCount > 0 && (
                      <Badge className="bg-blue-50 text-blue-700 border border-blue-200 flex items-center gap-1">
                        <RotateCw size={10} /> Resent ×{n.resendCount}
                      </Badge>
                    )}
                  </div>
                  <p className="text-sm text-gray-700 mb-2 break-words">{n.message}</p>
                  {n.imageUrl && (
                    <img src={n.imageUrl} alt="" className="rounded-lg max-h-40 mb-2 object-cover" />
                  )}
                  <div className="text-xs text-gray-500 flex flex-wrap items-center gap-3">
                    <span>{n.recipientCount} recipient{n.recipientCount === 1 ? '' : 's'}</span>
                    {n.sentByName && <span>· by {n.sentByName}</span>}
                    <span>· {format(new Date(n.createdAt), 'dd MMM yyyy, hh:mm a')}</span>
                    {n.lastResendAt && (
                      <span>· last resent {format(new Date(n.lastResendAt), 'dd MMM, hh:mm a')}</span>
                    )}
                  </div>
                </div>
                <div className="flex flex-col gap-1.5 items-end">
                  <Button
                    size="sm"
                    variant="outline"
                    data-testid={`resend-notif-${n.id}`}
                    onClick={() => resend(n)}
                    disabled={resendingId === n.id}
                    className="text-cyan-700 border-cyan-200 hover:bg-cyan-50"
                  >
                    <RotateCw size={14} className={`mr-1 ${resendingId === n.id ? 'animate-spin' : ''}`} />
                    {resendingId === n.id ? 'Sending…' : 'Resend'}
                  </Button>
                  <Button size="sm" variant="ghost" data-testid={`delete-notif-${n.id}`} onClick={() => remove(n)}>
                    <Trash2 size={16} className="text-red-600" />
                  </Button>
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* COMPOSE DIALOG */}
      <Dialog open={showCompose} onOpenChange={(o) => { setShowCompose(o); if (!o) resetCompose(); }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="compose-dialog">
          <DialogHeader>
            <DialogTitle>Send a Notification</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Audience *</Label>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-2">
                {Object.entries(AUDIENCE_META).map(([k, v]) => {
                  const Icon = v.icon;
                  const active = audience === k;
                  return (
                    <button
                      key={k}
                      type="button"
                      data-testid={`audience-${k}`}
                      onClick={() => { setAudience(k); setSelectedRecipients([]); if (k === 'ALL') setRecipientMode('everyone'); }}
                      className={`p-3 rounded-lg border text-left transition-all ${
                        active ? 'border-orange-500 ring-2 ring-orange-200 bg-orange-50' : 'border-gray-200 hover:bg-gray-50'
                      }`}
                    >
                      <div className="flex items-center gap-2 mb-1">
                        <Icon size={16} style={{ color: v.color }} />
                        <span className="font-medium text-sm">{v.label}</span>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            {audience !== 'ALL' && (
              <div>
                <Label>Recipients</Label>
                <div className="flex gap-2 mt-2">
                  {['everyone', 'specific'].map((m) => (
                    <button
                      key={m}
                      type="button"
                      data-testid={`recipient-mode-${m}`}
                      onClick={() => setRecipientMode(m)}
                      className={`px-3 py-1.5 text-xs rounded-full border ${
                        recipientMode === m ? 'bg-orange-50 border-orange-300 text-orange-700' : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50'
                      }`}
                    >
                      {m === 'everyone' ? `Everyone in ${AUDIENCE_META[audience].label}` : 'Specific recipients'}
                    </button>
                  ))}
                </div>

                {recipientMode === 'specific' && (
                  <div className="mt-3 border rounded-lg p-3 bg-gray-50">
                    <div className="relative">
                      <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                      <Input
                        data-testid="recipient-search"
                        placeholder="Search by name, phone or email..."
                        className="pl-8 h-9 bg-white"
                        value={recipientQuery}
                        onChange={(e) => setRecipientQuery(e.target.value)}
                      />
                    </div>

                    {selectedRecipients.length > 0 && (
                      <div className="flex flex-wrap gap-1.5 mt-3">
                        {selectedRecipients.map((r) => (
                          <Badge key={r.id} className="bg-orange-100 text-orange-800 border-0 flex items-center gap-1 pl-2 pr-1 py-1">
                            {r.name}
                            <button onClick={() => toggleRecipient(r)} className="hover:bg-orange-200 rounded-full">
                              <X size={12} />
                            </button>
                          </Badge>
                        ))}
                      </div>
                    )}

                    <div className="mt-3 max-h-40 overflow-y-auto bg-white border rounded">
                      {recipientResults.length === 0 ? (
                        <p className="text-xs text-gray-500 px-3 py-2">Type a name to search...</p>
                      ) : (
                        recipientResults.map((r) => {
                          const checked = selectedRecipients.find((x) => x.id === r.id);
                          return (
                            <button
                              key={r.id}
                              type="button"
                              onClick={() => toggleRecipient(r)}
                              className={`w-full flex items-center gap-2 px-3 py-2 text-sm border-b last:border-b-0 hover:bg-gray-50 text-left ${
                                checked ? 'bg-orange-50' : ''
                              }`}
                            >
                              <span className="flex-1">
                                <span className="font-medium text-gray-800">{r.name}</span>
                                <span className="text-xs text-gray-500 ml-2">{r.phone || r.email || ''}</span>
                              </span>
                              {checked && <CheckCircle size={14} className="text-orange-600" />}
                            </button>
                          );
                        })
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}

            <div>
              <Label>Title *</Label>
              <Input data-testid="notif-title-input" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Special offer just for you!" />
            </div>
            <div>
              <Label>Message *</Label>
              <Textarea data-testid="notif-message-input" rows={4} value={message} onChange={(e) => setMessage(e.target.value)} placeholder="Get 20% off on your next order..." />
            </div>
            <div>
              <Label>Image URL (optional)</Label>
              <div className="relative">
                <ImageIcon size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <Input className="pl-8" value={imageUrl} onChange={(e) => setImageUrl(e.target.value)} placeholder="https://..." />
              </div>
              {imageUrl && <img src={imageUrl} alt="preview" className="mt-2 rounded-lg max-h-40 object-cover" />}
            </div>

            <Button data-testid="notif-send-button" onClick={send} disabled={sending} className="w-full" style={{ backgroundColor: '#C2410C' }}>
              <Send size={16} className="mr-2" />
              {sending ? 'Sending...' : 'Send Notification'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Notifications;
